import { inject, Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';

import { ConfigSettingsService } from '../config-settings/config-settings.service';

/** Result of converting a PDF blob to a thumbnail, with the original page count. */
export interface PdfThumbResult {
  thumbnail: string;
  /** Undefined when the page count could not be determined client-side; the backend then falls back to 1. */
  pageCount?: number;
}

/** Thrown by {@link ConverterService.blobPdfToBase64Thumb} when the PDF requires a password to open. */
export class PdfPasswordProtectedError extends Error {
  constructor() {
    super('Failed to create thumbnail: PDF is password protected');
    this.name = 'PdfPasswordProtectedError';
  }
}

/**
 * Service for converting between different data formats and handling image thumbnails.
 * Provides utilities for converting between base64, Uint8Array, blobs, and other formats,
 * as well as image thumbnail generation and manipulation.
 */
@Injectable({
  providedIn: 'root',
})
export class ConverterService {
  private readonly configService = inject(ConfigSettingsService);
  /** Maximum height for generated thumbnails in pixels */
  private static readonly maxHeight = 72;
  /** Maximum width for generated thumbnails in pixels */
  private static readonly maxWidth = 72;

  /**
   * Cleans up a canvas element by resetting its dimensions and removing it from the DOM.
   * Used to prevent memory leaks, especially on iOS devices.
   *
   * @param {HTMLCanvasElement} canvas - The canvas element to clean up
   */
  public static cleanUpCanvas(canvas: HTMLCanvasElement): void {
    canvas.width = 0;
    canvas.height = 0;
    canvas.remove();
  }

  /**
   * Creates a thumbnail data URI from an image by scaling it down while maintaining aspect ratio.
   * Uses a two-step canvas process to ensure smooth scaling.
   *
   * @param {HTMLImageElement} img - The source image to create a thumbnail from
   * @returns {string} A data URI containing the thumbnail image
   * @throws {Error} If unable to get 2D context from canvas
   */
  public static createThumbDataUriFromImage(img: HTMLImageElement): string {
    const ratio = ConverterService.computeRatio(img);
    const targetWidth = img.width * ratio;
    const targetHeight = img.height * ratio;

    const canvasCopy = document.createElement('canvas');
    const copyContext = canvasCopy.getContext('2d');
    if (!copyContext) {
      throw new Error('Failed to get 2D context from canvas.');
    }

    canvasCopy.width = targetWidth;
    canvasCopy.height = targetHeight;
    copyContext.drawImage(img, 0, 0, img.width, img.height, 0, 0, targetWidth, targetHeight);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas.');
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    ctx.drawImage(canvasCopy, 0, 0, canvasCopy.width, canvasCopy.height, 0, 0, canvas.width, canvas.height);

    ConverterService.cleanUpCanvas(canvasCopy);
    return canvas.toDataURL();
  }

  /**
   * Converts a base64 encoded string to a Uint8Array.
   * Removes any non-base64 characters before conversion.
   *
   * @param {string} base64 - The base64 encoded string to convert
   * @returns {Uint8Array<ArrayBuffer>} A Uint8Array representing the decoded binary data
   */
  public base64ToUintArray(base64: string): Uint8Array<ArrayBuffer> {
    // Keep only valid base64 characters and padding
    const cleanedBase64 = base64.replace(/[^A-Za-z0-9+/=]/g, '');
    const binaryString = window.atob(cleanedBase64);
    const uint8Array = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }

    return uint8Array;
  }

  /**
   * Converts a Blob to a base64 encoded string.
   *
   * @param {Blob} blob - The Blob to convert
   * @returns {Promise<string>} A Promise that resolves with the base64 encoded string
   */
  public blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve: (value: string) => void, reject) => {
      const fileReader = new FileReader();

      fileReader.onerror = () => {
        reject(new Error('Failed to read blob'));
      };

      fileReader.onload = () => {
        try {
          const result = fileReader.result as string;
          resolve(result);
        } catch {
          reject(new Error('Failed to process blob data'));
        }
      };

      fileReader.readAsDataURL(blob);
    });
  }

  /**
   * Converts a Blob to a base64 encoded thumbnail.
   * Creates a thumbnail from the image while maintaining aspect ratio.
   *
   * @param {Blob} blob - The image Blob to create a thumbnail from
   * @returns {Promise<string>} A Promise that resolves with the base64 encoded thumbnail
   */
  public async blobToBase64Thumb(blob: Blob): Promise<string> {
    const einreichungV3Activated = this.configService.appSettings.einreichungV3Activated;

    if (!blob.type.startsWith('image/') && !(einreichungV3Activated && blob.type === 'application/pdf')) {
      throw new Error(
        'Failed to create thumbnail: Invalid mime type - expected image/* or application/pdf (when v3 is activated)',
      );
    }

    try {
      const base64Image = await this.blobToBase64(blob);

      // Validate base64 image data
      if (!base64Image.startsWith('data:image/') && !einreichungV3Activated) {
        throw new Error('Invalid image data');
      }

      return await new Promise<string>((resolve, reject) => {
        const img = new Image();

        const cleanup = (): void => {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        };

        img.onerror = () => {
          cleanup();
          reject(new Error('Failed to load image'));
        };

        img.onload = () => {
          try {
            const result = ConverterService.createThumbDataUriFromImage(img);
            cleanup();
            resolve(result);
          } catch (error) {
            cleanup();
            reject(error);
          }
        };

        img.src = base64Image;
      });
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to create thumbnail: ${error.message}`);
      } else {
        throw new Error('Failed to create thumbnail: Unknown error');
      }
    }
  }

  /**
   * Converts a Blob to a Uint8Array.
   *
   * @param {Blob} blob - The Blob to convert
   * @returns {Promise<Uint8Array>} A Promise that resolves with the Uint8Array
   */
  public blobToUint8Array(blob: Blob): Promise<Uint8Array<ArrayBuffer>> {
    const fileReader = new FileReader();
    return new Promise((resolve: (value: Uint8Array<ArrayBuffer>) => void) => {
      fileReader.onload = (event: ProgressEvent<FileReader>) => {
        resolve(new Uint8Array(event.target?.result as ArrayBuffer));
      };
      fileReader.readAsArrayBuffer(blob);
    });
  }

  /**
   * Converts an image data URI to a base64 encoded thumbnail.
   *
   * @param {string} imageDataUri - The image data URI to convert
   * @returns {Promise<string>} A Promise that resolves with the base64 encoded thumbnail
   */
  public dataUriToBase64Thumb(imageDataUri: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const img = new Image();

      img.onerror = () => reject();
      img.onload = () => resolve(ConverterService.createThumbDataUriFromImage(img));

      img.src = imageDataUri;
    });
  }

  /**
   * Creates a thumbnail from a File object.
   *
   * @param {File} file - The image file to create a thumbnail from
   * @returns {Promise<string>} A Promise that resolves with the thumbnail as a base64 encoded string
   */
  public async getThumbnail(file: File): Promise<string> {
    try {
      const imageDataUri = URL.createObjectURL(file);
      try {
        const thumbnail = await this.dataUriToBase64Thumb(imageDataUri);
        return thumbnail;
      } finally {
        URL.revokeObjectURL(imageDataUri);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get thumbnail: ${error.message}`);
      } else {
        throw new Error('Failed to get thumbnail: Unknown error');
      }
    }
  }

  /**
   * Converts a hexadecimal string to a Uint8Array.
   *
   * @param {string} hexString - The hexadecimal string to convert
   * @returns {Uint8Array} The resulting Uint8Array
   * @throws {Error} If the hexString length is not even
   */
  public hexStringToUint8Array(hexString: string): Uint8Array<ArrayBuffer> {
    if (hexString.length % 2 !== 0) {
      throw new Error('Invalid hexString');
    }

    const arrayBuffer = new Uint8Array(hexString.length / 2);
    for (let i = 0; i < hexString.length; i += 2) {
      arrayBuffer[i / 2] = this.hexPairToByte(hexString.slice(i, i + 2));
    }

    return arrayBuffer;
  }

  /**
   * Merges multiple Uint8Arrays into a single Uint8Array.
   *
   * @param {Uint8Array[]} uInt8List - Array of Uint8Arrays to merge
   * @returns {Uint8Array} A single Uint8Array containing all the data
   */
  public mergeUInt8Arrays(uInt8List: Uint8Array[]): Uint8Array {
    const totalLength = uInt8List.reduce((acc, arr) => acc + arr.length, 0);
    const merged = new Uint8Array(totalLength);
    let offset = 0;

    for (const arr of uInt8List) {
      merged.set(arr, offset);
      offset += arr.length;
    }

    return merged;
  }

  /**
   * Converts a string to a Uint16Array using UTF-16 encoding.
   *
   * @param {string} str - The string to convert
   * @returns {Uint16Array} The resulting Uint16Array
   */
  public stringToUint16Array(str: string): Uint16Array<ArrayBuffer> {
    const buf = new ArrayBuffer(str.length * 2); // 2 bytes for each char
    const bufView = new Uint16Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }

    return bufView;
  }

  /**
   * Converts a Uint8Array or Uint16Array to a base64 encoded string.
   *
   * @param {Uint8Array | Uint16Array | Uint8Array[] | null} data - The data to convert
   * @returns {string | null} The base64 encoded string or null if data is null
   */
  public uIntArrayToBase64(data: Uint16Array | Uint8Array | Uint8Array[] | null): string | null {
    if (data == null) {
      return null;
    }
    return window.btoa(this.uIntArrayToString(Array.isArray(data) ? this.mergeUInt8Arrays(data) : data));
  }

  /**
   * Converts a Uint8Array or Uint16Array to a string.
   *
   * @param {Uint8Array | Uint16Array | ArrayBuffer} uintArray - The data to convert
   * @returns {string} The resulting string
   */
  public uIntArrayToString(uintArray: Uint16Array | Uint8Array | ArrayBuffer): string {
    const CHUNK_SIZE = 8192; // Process in chunks to avoid call stack size exceeded
    const arr = new Uint16Array(uintArray);
    let result = '';

    for (let i = 0; i < arr.length; i += CHUNK_SIZE) {
      const chunk = arr.slice(i, i + CHUNK_SIZE);
      result += String.fromCharCode.apply(null, Array.from(chunk));
    }

    return result;
  }

  /**
   * Converts a Blob pdf to a base64 encoded thumbnail with number of pages.
   * Creates a thumbnail from PDF file.
   *
   * @param {Blob} blob - The pdf Blob to create a thumbnail from
   * @returns {Promise<PdfThumbResult>} A Promise that resolves with the base64 encoded thumbnail and the PDF's page
   *   count. The page count is undefined (and not drawn onto the thumbnail) when it could not be determined.
   * @throws {PdfPasswordProtectedError} If the PDF requires a password to open
   */
  public async blobPdfToBase64Thumb(blob: Blob): Promise<PdfThumbResult> {
    if (!blob.type.startsWith('application/pdf')) {
      throw new Error('Failed to create thumbnail: Invalid mime type - expected application/pdf');
    }

    let pdf: pdfjsLib.PDFDocumentProxy | undefined;
    try {
      const arrayBuffer = await blob.arrayBuffer();
      pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const firstPage = await pdf.getPage(1);

      const viewport = firstPage.getViewport({ scale: 1 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      if (!context) {
        throw new Error('Could not get canvas context');
      }

      await firstPage.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      let img = new Image();
      img.src = canvas.toDataURL();
      await new Promise<void>((resolve) => (img.onload = () => resolve()));

      let thumbnail = ConverterService.createThumbDataUriFromImage(img);

      img = new Image();
      img.src = thumbnail;
      await new Promise<void>((resolve) => (img.onload = () => resolve()));
      context.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = img.width;
      canvas.height = img.height;

      context.drawImage(img, 0, 0, img.width, img.height, 0, 0, img.width, img.height);

      const pageCount = pdf.numPages > 0 ? pdf.numPages : undefined;

      if (pageCount !== undefined) {
        // Draw the page count inside the canvas
        context.fillStyle = 'black';
        context.font = 'bold 16px system-ui';
        context.imageSmoothingEnabled = false;

        const text = `${pageCount}`;
        const padding = 5;
        const textWidth = context.measureText(text).width;

        context.fillText(text, canvas.width - textWidth - padding, canvas.height - padding);
      }

      thumbnail = canvas.toDataURL();

      ConverterService.cleanUpCanvas(canvas);

      return { thumbnail, pageCount };
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'name' in error && error['name'] === 'PasswordException') {
        throw new PdfPasswordProtectedError();
      }
      if (error instanceof Error) {
        throw new Error(`Failed to create thumbnail: ${error.message}`);
      } else {
        throw new Error('Failed to create thumbnail: Unknown error');
      }
    } finally {
      // Release the worker memory held by the parsed document
      await pdf?.destroy();
    }
  }

  /**
   * Converts a hexadecimal pair to a byte.
   *
   * @param {string} hexPair - Two-character hexadecimal string
   * @returns {number} The byte value
   * @throws {Error} If the hexPair is invalid
   */
  private hexPairToByte(hexPair: string): number {
    const byte = parseInt(hexPair, 16);
    if (isNaN(byte)) {
      throw new Error('Invalid hexPair');
    }
    return byte;
  }

  /**
   * Computes the scaling ratio for an image to fit within maxWidth and maxHeight
   * while maintaining aspect ratio.
   *
   * @param {HTMLImageElement} image - The image to compute the ratio for
   * @returns {number} The scaling ratio to apply
   */
  private static computeRatio(image: HTMLImageElement): number {
    if (image.width > ConverterService.maxWidth) {
      return ConverterService.maxWidth / image.width;
    }

    if (image.height > ConverterService.maxHeight) {
      return ConverterService.maxHeight / image.height;
    }

    return 1;
  }
}
